<template>
  <div class="p-8 bg-white dark:bg-[#0c1427] rounded-md m-4">
    <h2 class="text-2xl font-bold mb-6 text-black dark:text-white">{{ $t('dashboard.title') }}</h2>
    
    <div class="grid gap-4 mb-8">
      <div class="p-4 bg-gray-50 dark:bg-[#15203c] rounded-md">
        <p class="mb-2 font-semibold">{{ $t('common.chooseLanguage') }}:</p>
        <div class="flex space-x-4">
          <button 
            @click="changeLanguage('en')" 
            class="px-4 py-2 rounded-md"
            :class="currentLanguage === 'en' ? 'bg-primary-500 text-white' : 'bg-gray-200 dark:bg-[#172036] text-black dark:text-white'"
          >
            English
          </button>
          <button 
            @click="changeLanguage('he')" 
            class="px-4 py-2 rounded-md"
            :class="currentLanguage === 'he' ? 'bg-primary-500 text-white' : 'bg-gray-200 dark:bg-[#172036] text-black dark:text-white'"
          >
            עברית
          </button>
          <button 
            @click="changeLanguage('ar')" 
            class="px-4 py-2 rounded-md"
            :class="currentLanguage === 'ar' ? 'bg-primary-500 text-white' : 'bg-gray-200 dark:bg-[#172036] text-black dark:text-white'"
          >
            العربية
          </button>
        </div>
      </div>
      
      <div class="p-4 bg-gray-50 dark:bg-[#15203c] rounded-md">
        <h3 class="text-xl font-semibold mb-3 text-black dark:text-white">{{ $t('dashboard.welcome') }}</h3>
        
        <div class="grid gap-3">
          <p class="text-black dark:text-white">{{ $t('navbar.notifications') }}</p>
          <p class="text-black dark:text-white">{{ $t('navbar.messages') }}</p>
          <p class="text-black dark:text-white">{{ directT('common.rtlMode') }}: {{ isRtl ? 'Enabled' : 'Disabled' }}</p>
          <p class="text-black dark:text-white">{{ $t('common.search') }}</p>
          <p class="text-black dark:text-white">{{ $t('dashboard.sales') }}: 1,245</p>
          <p class="text-black dark:text-white">{{ $t('dashboard.revenue') }}: $12,345</p>
        </div>
      </div>
    </div>
    
    <div class="text-right">
      <button
        @click="toggleDirection"
        class="inline-flex items-center px-4 py-2 border border-primary-500 text-primary-500 rounded-md hover:bg-primary-500 hover:text-white transition-colors duration-300"
      >
        {{ directT('common.rtlMode') }}
      </button>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, ref, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { setLanguage, type SupportedLanguage } from '@/i18n';

export default defineComponent({
  name: 'TestTranslation',
  setup() {
    const { locale, t } = useI18n();
    const currentLanguage = ref<SupportedLanguage>(locale.value as SupportedLanguage);
    const direction = ref(document.body.getAttribute('dir') || 'ltr');
    
    // Direct translation function
    const directT = (key: string): string => {
      return t(key);
    };
    
    const isRtl = computed(() => direction.value === 'rtl');
    
    const changeLanguage = (lang: SupportedLanguage) => {
      currentLanguage.value = lang;
      setLanguage(lang);
      direction.value = document.body.getAttribute('dir') || 'ltr';
    };
    
    const toggleDirection = () => {
      const newDirection = direction.value === 'ltr' ? 'rtl' : 'ltr';
      direction.value = newDirection;
      document.body.setAttribute('dir', newDirection);
      localStorage.setItem('direction', newDirection);
      
      // Change language based on direction
      if (newDirection === 'rtl' && !['he', 'ar'].includes(currentLanguage.value)) {
        changeLanguage('he');
      } else if (newDirection === 'ltr' && ['he', 'ar'].includes(currentLanguage.value)) {
        changeLanguage('en');
      }
    };
    
    return {
      currentLanguage,
      isRtl,
      changeLanguage,
      toggleDirection,
      directT
    };
  }
});
</script> 